/*
 * pogi-quiz - gamified 7-step personalized product finder.
 * Flow: intro -> name (+gender) -> 6 pain-point questions (each with a Pogi quip
 * reaction) -> "sniffing out the routine" reveal -> RESULT: a character card that
 * crowns the dog an archetype (Mud Magnet, Tiny Tornado, Wrinkle Royalty, etc.)
 * with RPG-style stat bars derived from the answers, the matching product routine,
 * and an email gate that unlocks 15% off + writes to KLAVIYO (client subscribe)
 * with every answer saved as profile properties. Config (products incl. compostable,
 * klaviyo ids, copy) comes from the section JSON so it stays editable.
 */
import { CartUpdateEvent } from '@theme/events';

/* Market-currency guard for JS-DERIVED secondary prices. The quiz catalog prices are
   the shop BASE-currency cents (USD) and money() always prepends '$' — so on a non-USD
   market it would show a wrong '$' figure. The real prices localize via Liquid `| money`
   on the PDP. To never show a wrong derived price off-USD we HIDE the quiz result-card
   price + the "Your pick/kit" subtotal when the active market currency isn't USD (the
   shop base). On USD this is true → identical behavior. */
const SHOW_DERIVED = !(window.Shopify && Shopify.currency && Shopify.currency.active) || Shopify.currency.active === 'USD';

class PogiQuiz extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    try { this.cfg = JSON.parse(this.querySelector('[data-pogi-quiz-config]').textContent); } catch (_) { this.cfg = {}; }
    this.mount = this.querySelector('[data-pogi-quiz-mount]') || this;
    this.phase = 'intro';
    this.qi = 0;
    this.answers = {};
    this.reaction = '';
    this.cap = { sent: false };
    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('input', this.#onInput);
    document.addEventListener('keydown', this.#onKey);
    this._soundBtn = this.querySelector('.pogi-quiz__sound');
    try { this.muted = localStorage.getItem('pogiQuizMuted') === '1'; } catch (_) { this.muted = false; }
    this.#updateSoundIcon();
    // deep-link: /pages/quiz?start (homepage CTA) jumps straight to the name step
    try { const u = new URL(window.location.href); if (u.searchParams.has('start') || (window.location.hash || '').indexOf('start') >= 0) { this.phase = 'q'; this.qi = 0; } } catch (_) {}
    this.render();
    if (this.phase === 'q') { this.#trackStart(); this.#focusName(); }
  }

  disconnectedCallback() { try { document.removeEventListener('keydown', this.#onKey); } catch (_) {} }

  /* ---------- sound (Web Audio oscillators, no asset files) ---------- */
  #ac() { if (this._sfxCtx) return this._sfxCtx; try { this._sfxCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch (_) { this._sfxCtx = null; } return this._sfxCtx; }
  #beep(freq, dur, type, when, vol) {
    if (this.muted) return; const ac = this.#ac(); if (!ac) return;
    try { if (ac.state === 'suspended') ac.resume(); } catch (_) {}
    const t = ac.currentTime + (when || 0);
    const o = ac.createOscillator(), g = ac.createGain();
    o.type = type || 'square'; o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t); g.gain.linearRampToValueAtTime(vol || 0.06, t + 0.012); g.gain.exponentialRampToValueAtTime(0.0001, t + (dur || 0.12));
    o.connect(g); g.connect(ac.destination); o.start(t); o.stop(t + (dur || 0.12) + 0.03);
  }
  #sfxSelect() { this.#beep(620, 0.07, 'square', 0, 0.05); this.#beep(880, 0.09, 'square', 0.05, 0.045); }
  #sfxWin() { [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => this.#beep(f, 0.2, 'triangle', i * 0.12, 0.07)); this.#beep(1318.5, 0.32, 'triangle', 0.48, 0.05); }
  #toggleSound() { this.muted = !this.muted; try { localStorage.setItem('pogiQuizMuted', this.muted ? '1' : '0'); } catch (_) {} this.#updateSoundIcon(); if (!this.muted) this.#sfxSelect(); }
  #updateSoundIcon() { if (!this._soundBtn) return; this._soundBtn.textContent = this.muted ? '🔇' : '🔊'; this._soundBtn.setAttribute('aria-pressed', String(!this.muted)); }

  get COMPANY() { return this.cfg.klaviyoCompany || 'NV7ZgX'; }
  get LIST() { return this.cfg.klaviyoList || 'Lt2k5Y'; }
  get PRODUCTS() { return this.cfg.products || {}; }
  dn() { const n = (this.answers.name || '').trim(); return n || 'your dog'; }
  dnPoss() { const n = (this.answers.name || '').trim(); return n ? n + '’s' : 'your dog’s'; }
  goodLabel() { return this.answers.gender === 'girl' ? 'Good Girl' : this.answers.gender === 'boy' ? 'Good Boy' : 'Good Dog'; }

  /* ---------- questions ---------- */
  get STEPS() {
    const n = this.dn();
    return [
      { id: 'name', type: 'text', q: 'First things first, who are we getting sorted?', sub: 'Tell us your dog’s name and we’ll make this personal.', placeholder: 'Your dog’s name' },
      { id: 'stage', q: `How old is ${n}?`, sub: 'It helps us tune the routine to their stage.', options: [
        { value: 'puppy', label: 'A puppy', desc: 'Under a year, still learning the rules', emoji: '🍼' },
        { value: 'adult', label: 'An adult', desc: 'Settled into the daily routine', emoji: '🐕' },
        { value: 'senior', label: 'A senior', desc: 'Taking it easier these days', emoji: '🧓' },
        { value: 'pack', label: 'A whole pack', desc: 'More than one to keep clean', emoji: '🐾' } ] },
      { id: 'coat', q: `When ${n} comes back inside, what are you usually dealing with?`, sub: 'Pick what happens most.', options: [
        { value: 'muddy', label: 'Muddy paws', desc: 'Prints tracked across the floor', emoji: '🐾' },
        { value: 'coat', label: 'A dirty coat and belly', desc: 'Grime from the chest down', emoji: '🛁' },
        { value: 'folds', label: 'Wrinkles and face folds', desc: 'Flat faces and folds that trap gunk', emoji: '🐶' },
        { value: 'clean', label: 'Honestly, pretty clean', desc: 'A quick once-over at most', emoji: '✨' } ] },
      { id: 'skin', q: `How is ${n}’s skin, and what scent do you like?`, sub: null, options: [
        { value: 'sensitive', label: 'Sensitive or itchy', desc: 'Keep it fragrance-free', emoji: '🌿' },
        { value: 'fresh', label: 'Normal skin', desc: 'A light fresh scent is nice', emoji: '🌸' },
        { value: 'unsure', label: 'Not sure', desc: 'We’ll play it safe and gentle', emoji: '🤷' } ] },
      { id: 'bags', q: `What matters most for ${n}’s poop bags?`, sub: 'So we grab the right roll.', options: [
        { value: 'leakproof', label: 'Strong and leak-proof', desc: 'Thick bags, no nasty surprises', emoji: '🛡️' },
        { value: 'compostable', label: 'Certified compostable', desc: 'The certified-compostable option', emoji: '🌱' },
        { value: 'runout', label: 'Never running out', desc: 'Auto-refills so you’re never caught short', emoji: '🔄' } ] },
      { id: 'indoor', q: `Any indoor potty needs for ${n}?`, sub: null, options: [
        { value: 'training', label: 'Training or accidents', desc: 'House-training or the odd accident', emoji: '🚼' },
        { value: 'apartment', label: 'Apartment, no yard', desc: 'Indoor potty is the routine', emoji: '🏢' },
        { value: 'weather', label: 'Bad-weather backup', desc: 'For storms and cold snaps', emoji: '🌧️' },
        { value: 'none', label: 'Nope, all outdoors', desc: 'No indoor needs', emoji: '✅' } ] },
      { id: 'travel', q: `Where does ${n} get into the most mischief?`, sub: 'So we grab the right pack for your life.', options: [
        { value: 'home', label: 'Mostly around home', desc: 'Backyard and neighborhood walks', emoji: '🏡' },
        { value: 'hikes', label: 'Trails and hikes', desc: 'Off on adventures, mud included', emoji: '🥾' },
        { value: 'roadtrips', label: 'Road trips and travel', desc: 'Always on the go together', emoji: '🚗' },
        { value: 'everywhere', label: 'Honestly, everywhere', desc: 'Homebody one day, explorer the next', emoji: '🌍' } ] },
      { id: 'size', q: `Last one, how big is ${n}?`, sub: 'Roughly is fine, it helps us size things right.', options: [
        { value: 'small', label: 'Small', desc: 'Up to ~25 lbs', emoji: '🤏' },
        { value: 'medium', label: 'Medium', desc: '~25 to 60 lbs', emoji: '🐕' },
        { value: 'large', label: 'Large', desc: '~60 to 90 lbs', emoji: '💪' },
        { value: 'giant', label: 'Giant', desc: '90 lbs and up', emoji: '🦣' } ] },
    ];
  }

  get REACTIONS() {
    return {
      stage: { puppy: 'Oh, a baby. Buckle up. 🍼', adult: 'Prime of life. Respect.', senior: 'A wise old soul. 🎩', pack: 'A whole crew? Legend.' },
      coat: { muddy: 'The eternal mud saga.', coat: 'Grime from nose to tail, got it.', folds: 'Those folds though. 😍', clean: 'A tidy one. Rare breed.' },
      skin: { sensitive: 'Sensitive skin, noted. Gentle it is.', fresh: 'A fresh-scent fan.', unsure: 'We’ll keep it gentle, promise.' },
      bags: { leakproof: 'Strong and leak-proof. The only way.', compostable: 'A planet person. 🌱 Respect.', runout: 'Never caught empty-handed again.' },
      indoor: { training: 'Indoor backup, coming right up.', apartment: 'Apartment life, we got you.', weather: 'Rainy-day insurance. Smart.', none: 'All handled inside. Nice.' },
      travel: { home: 'A cozy homebody. Love that.', hikes: 'Trail dog! Adventure mode. 🥾', roadtrips: 'Shotgun co-pilot. Iconic. 🚗', everywhere: 'Down for anything. Legend.' },
      size: { small: 'Pocket-sized. 🥹', medium: 'The classic.', large: 'A big unit. 💪', giant: 'A literal horse. We love it.' },
    };
  }

  /* ---------- scoring + archetype + stats ---------- */
  get SCORES() {
    return {
      stage: { puppy: { pads: 2, wipes: 1 }, adult: { bags: 1 }, senior: { pads: 2, wipes: 1 }, pack: { bags: 1, wipes: 1, pads: 1 } },
      coat: { muddy: { wipes: 3 }, coat: { wipes: 3 }, folds: { wipes: 3 }, clean: {} },
      skin: { sensitive: { wipes: 1 }, fresh: {}, unsure: {} },
      bags: { leakproof: { bags: 3 }, compostable: { bags: 3 }, runout: { bags: 3 } },
      indoor: { training: { pads: 3 }, apartment: { pads: 3 }, weather: { pads: 3 }, none: {} },
      travel: { home: {}, hikes: { wipes: 1, bags: 1 }, roadtrips: { wipes: 1 }, everywhere: { wipes: 1 } },
    };
  }
  padSizeFor(s) { return s === 'small' ? 'Medium' : (s === 'large' || s === 'giant') ? 'X-Large' : 'Large'; }

  recommend(a) {
    const totals = { wipes: 0, bags: 0, pads: 0 };
    ['stage', 'coat', 'skin', 'bags', 'indoor', 'travel'].forEach((k) => {
      const add = (this.SCORES[k] || {})[a[k]] || {};
      Object.keys(add).forEach((p) => (totals[p] += add[p]));
    });
    const wantsCompostable = a.bags === 'compostable';
    const max = Math.max(totals.wipes, totals.bags, totals.pads);
    const strong = Object.keys(totals).filter((p) => totals[p] >= 3 && totals[p] >= max - 1);
    const isKit = strong.length >= 2;
    const primary = Object.keys(totals).sort((x, y) => totals[y] - totals[x])[0];
    return { isKit, primary, strong: strong.length >= 2 ? strong : [primary], wantsCompostable, totals };
  }
  prodKey(p, rec) { return (p === 'bags' && rec.wantsCompostable) ? 'compostable' : p; }

  /* Deduce the matching variant from the emitted catalog.
     POGI FIX 2026-07-23 (audit L8-001): option TEXT is LOCALIZED per storefront —
     the old EN-literal matching fell back to list[0] on every non-EN locale (wrong
     variant added from the quiz). The locale-stable contract: `oi` = the variant's
     option-value INDICES (value ORDER is locale-invariant; emitted by
     pogi-variant-oi.liquid), plus first-number count matching where a count
     disambiguates (digits survive every locale, incl. ja). Semantic anchors,
     pinned to the EN admin value order (re-anchor here if values are REORDERED —
     the same exposure as the buy-box default-variant doctrine):
       wipes  Format [Tubs, Travel]=idx 0/1 · Scent [Fragrance-Free, GreenTea]=0/1 · Qty by count
       pads   Quantity by count (40) · Size [Large, Medium, XL]=idx 0/1/2
       bags   Format Refill=0 · Size by count (30) · Scent [Fresh, Unscented]=0/1
       comp   Format Refill=0 · Size by count (18)
     Returns {id, o1, o2, o3, oi, img} or null. */
  wantsTravel(a) { return a.travel === 'hikes' || a.travel === 'roadtrips' || a.travel === 'everywhere'; }
  nums(s) { return (String(s == null ? '' : s).match(/\d[\d,]*/g) || []).map((n) => parseInt(n.replace(/,/g, ''), 10)); }
  matchOi(list, specs) {
    // specs: [[optIndex, want], ...] — want = value index (number) or {n: count}.
    // Counts match ANY exact numeric token of the value (Codex #11: first-number
    // broke on bags-first transcreations like pt «270 sacos · 18 rolos»; exact
    // tokens still reject 300/1000/180 for wants 30/100/18).
    return (list || []).find((v) => specs.every(([oi, want]) => {
      if (want && typeof want === 'object') return this.nums(v['o' + (oi + 1)]).includes(want.n);
      return Array.isArray(v.oi) && v.oi[oi] === want;
    })) || null;
  }
  variantFor(key, a) {
    const cat = (this.cfg && this.cfg.catalog) || {};
    const list = cat[key] || [];
    let m = null;
    if (key === 'wipes') { const travel = this.wantsTravel(a); m = this.matchOi(list, [[0, travel ? 1 : 0], [1, a.skin === 'fresh' ? 1 : 0], [2, { n: travel ? 120 : 100 }]]); }
    else if (key === 'pads') { const szIdx = ({ 'Medium': 1, 'Large': 0, 'X-Large': 2 })[this.padSizeFor(a.size)]; m = this.matchOi(list, [[0, { n: 40 }], [1, szIdx]]); }
    else if (key === 'bags') m = this.matchOi(list, [[0, 0], [1, { n: 30 }], [2, 1]]);
    else if (key === 'compostable') m = this.matchOi(list, [[0, 0], [1, { n: 18 }]]);
    // fall back to the product's first variant if the option structure drifted —
    // instrumented (Codex #11 P2): a silent fallback converts catalog drift into
    // a wrong purchasable variant; the warn makes probes/audits catch it.
    if (!m && list.length && !this._warnedQuizFallback) {
      this._warnedQuizFallback = true;
      console.warn('[pogi-quiz] no variant matched the semantic spec for "' + key + '"; falling back to first variant');
    }
    return m || list[0] || null;
  }
  variantLabel(key, a) {
    // Localized by construction: the matched variant's own (locale-rendered) option text.
    const v = this.variantFor(key, a);
    if (!v) return '';
    return [v.o1, v.o2, v.o3].filter(Boolean).slice(0, 2).join(' · ');
  }
  prodHref(prod, key, a) { const v = this.variantFor(key, a), id = v && v.id; return ((prod && prod.href) || '#') + (id ? '?variant=' + id : ''); }

  get ARCHETYPES() {
    return {
      wrinkle: { name: 'Wrinkle Royalty', emoji: '👑', tag: 'The folds are gorgeous. The upkeep is real.', kit: 'The Wrinkle Care Kit' },
      senior: { name: 'The Distinguished Senior', emoji: '🎩', tag: 'Old enough to know better. Too cute to care.', kit: 'The Gentle Kit' },
      tornado: { name: 'The Tiny Tornado', emoji: '🌪️', tag: 'Pint-sized, chaos-maxed, zero remorse.', kit: 'The Puppy Starter Kit' },
      mud: { name: 'The Mud Magnet', emoji: '🐾', tag: 'If it’s wet and filthy, they’ve already rolled in it.', kit: 'The Mud Magnet Kit' },
      pack: { name: 'The Pack Boss', emoji: '🐕', tag: 'One dog? They run a whole operation.', kit: 'The Pack Kit' },
      trail: { name: 'The Trailblazer', emoji: '🏞️', tag: 'Lives for the outdoors. Brings half of it home.', kit: 'The Daily Walk Kit' },
      giant: { name: 'The Gentle Giant', emoji: '🐻', tag: 'Huge heart, even huger paws.', kit: 'The Big Dog Kit' },
      city: { name: 'The City Slicker', emoji: '🏙️', tag: 'Small yard, big personality.', kit: 'The Apartment Kit' },
      sensitive: { name: 'The Sensitive Soul', emoji: '🌸', tag: 'Delicate, dignified, fragrance-free.', kit: 'The Sensitive Skin Kit' },
      eco: { name: 'The Eco Warrior', emoji: '🌱', tag: 'Saves the planet, one scoop at a time.', kit: 'The Green Kit' },
      couch: { name: 'The Couch Senator', emoji: '🛋️', tag: 'Governs the household from a horizontal position.', kit: 'The Tidy Kit' },
    };
  }
  /* playful, fixed "rarity" per archetype (sums to 100). Quiz flavor that makes a
     result feel select and shareable. Not real population data. */
  get RARITY() { return { wrinkle: 6, senior: 9, tornado: 12, mud: 19, pack: 7, trail: 15, giant: 8, city: 6, sensitive: 6, eco: 5, couch: 7 }; }
  archetypeKey(a) {
    if (a.coat === 'folds') return 'wrinkle';
    if (a.stage === 'senior') return 'senior';
    if (a.stage === 'puppy' && a.size !== 'large' && a.size !== 'giant') return 'tornado';
    if (a.coat === 'muddy' || a.coat === 'coat') return 'mud';
    if (a.stage === 'pack') return 'pack';
    if (a.travel === 'hikes' || a.travel === 'roadtrips' || a.travel === 'everywhere') return 'trail';
    if (a.size === 'giant' || a.size === 'large') return 'giant';
    if (a.indoor === 'apartment') return 'city';
    if (a.skin === 'sensitive') return 'sensitive';
    if (a.bags === 'compostable') return 'eco';
    return 'couch';
  }

  stats(a) {
    const clamp = (n) => Math.max(1, Math.min(5, n));
    let mess = ({ muddy: 4, coat: 5, folds: 3, clean: 1 })[a.coat] || 2;
    if (a.size === 'large') mess += 1; if (a.size === 'giant') mess += 1;
    let chaos = ({ puppy: 4, adult: 2, senior: 1, pack: 4 })[a.stage] || 2;
    if (a.indoor && a.indoor !== 'none') chaos += 1;
    const floof = ({ folds: 4, coat: 4, muddy: 3, clean: 2 })[a.coat] || 3;
    const sensitivity = a.skin === 'sensitive' ? 5 : a.skin === 'unsure' ? 3 : 2;
    return [
      { label: 'Mess Level', val: clamp(mess) },
      { label: 'Chaos', val: clamp(chaos) },
      { label: 'Floof', val: clamp(floof) },
      { label: 'Sensitivity', val: clamp(sensitivity) },
      { label: this.goodLabel(), val: 5, maxed: true },
    ];
  }

  /* Short, roughly-equal one-liners (no dog name, so lengths stay uniform).
     Wipes cloth = "bamboo fiber" by material only; "compostable" only on the
     compostable product; bags lead with 70% recycled. No banned green terms. */
  reasonFor(p, a) {
    const scent = a.skin === 'fresh' ? 'lightly scented' : 'fragrance-free';
    if (p === 'wipes') {
      const lead = a.coat === 'folds' ? 'For wrinkles and face folds'
        : a.coat === 'coat' ? 'For a grubby coat and belly'
        : a.coat === 'muddy' ? 'Quick work of muddy paws'
        : 'Gentle for daily cleanups';
      return `${lead}, bamboo fiber and ${scent}.`;
    }
    if (p === 'compostable') return 'Certified compostable bags, sturdy and leak-proof for the daily walk.';
    if (p === 'bags') return 'Thick, leak-proof bags, 70% recycled for the daily walk.';
    if (p === 'pads') {
      const lead = a.indoor === 'apartment' ? 'Made for apartment life'
        : a.indoor === 'weather' ? 'A reliable bad-weather backup'
        : a.stage === 'puppy' ? 'Perfect for house-training'
        : a.stage === 'senior' ? 'Gentle backup for accidents'
        : 'Handy for indoor accidents';
      return `${lead}, in the ${this.padSizeFor(a.size)} size.`;
    }
    return '';
  }

  /* ---------- events ---------- */
  #onClick = (e) => {
    // during a between-question reaction quip, any tap skips straight ahead
    if (this.reaction && this.phase === 'q') { clearTimeout(this._t); this.reaction = ''; this.#advance(); return; }
    const pup = e.target.closest('.pogi-quiz__avatar, .pogi-quiz__guide');
    if (pup) { this.#boop(pup); return; }
    const rare = e.target.closest('.pogi-quiz__rarity');
    if (rare) { this.#toast('Okay, we made that stat up. But your dog? Genuinely 1 of a kind. 💛'); this.#beep(660, 0.08, 'sine', 0, 0.05); return; }
    const el = e.target.closest('[data-q]');
    if (!el) return;
    const action = el.dataset.q;
    if (action === 'start') { this.#sfxSelect(); this.#trackStart(); this.phase = 'q'; this.qi = 0; this.reaction = ''; this.render(); this.#scrollTop(); this.#focusName(); }
    else if (action === 'sound') { this.#toggleSound(); }
    else if (action === 'back') { this.#back(); }
    else if (action === 'restart') { this.answers = {}; this.qi = 0; this.reaction = ''; this.cap = { sent: false }; this._celebrated = false; clearInterval(this._revInt); clearTimeout(this._t); clearTimeout(this._shakeT); this.phase = 'intro'; this.render(); this.#scrollTop(); }
    else if (action === 'gender') { let g = el.dataset.value; if (g === 'surprise') g = Math.random() < 0.5 ? 'girl' : 'boy'; this.answers = { ...this.answers, gender: g }; this.#sfxSelect(); this.render(); clearTimeout(this._t); this._t = setTimeout(() => this.#advance(), 720); }
    else if (action === 'name-skip') { clearTimeout(this._t); this.answers = { ...this.answers, name: '' }; this.#advance(); }
    else if (action === 'opt') { this.#choose(el.dataset.step, el.dataset.value); }
    else if (action === 'addcart') { this.#addToCart(el); }
    else if (action === 'subscribe') { this.#addToCart(el, true); }
    else if (action === 'share') { this.#share(); }
  };

  /* easter egg: cheeky line for fun/rude dog names (live as they type) */
  #nameWink(n) {
    const s = (n || '').trim().toLowerCase(); if (!s) return '';
    const celeb = { rex: 'A certified classic.', bella: 'The #1 dog name. Iconic.', luna: 'Moon dog. 🌙', max: 'Maximum good boy.', charlie: 'Charlie! Great pick.', cooper: 'Cooper, the legend.', daisy: 'Sweet as.', buddy: 'The original buddy.', milo: 'Milo! Chef’s kiss.', bailey: 'A good, good name.' };
    if (celeb[s]) return celeb[s];
    const naughty = ['fuck', 'shit', 'dick', 'cock', 'bitch', 'asshole', 'wank', 'pussy', 'cunt', 'poop', 'butt'];
    if (naughty.some((w) => s.indexOf(w) >= 0)) return 'Bold name. We’ll keep it between us. 😏';
    return '';
  }

  /* add the deduced variant(s) straight to cart so they never leave the quiz.
     sub=false -> one-time lines (a 2+ item kit auto-earns the 10% bundle discount);
     sub=true (the S&S nudge button) -> attaches each product's Recharge selling plan
     for a real subscription, falling back to the subscribe page if none exists.
     Uses Horizon's cart contract. */
  async #addToCart(btn, sub) {
    const a = this.answers, rec = this.recommend(a);
    const slots = rec.isKit ? rec.strong : [rec.primary];
    const arch = (this.ARCHETYPES[this.archetypeKey(a)] || {}).name || '';
    let value = 0, subbed = 0;
    const items = slots.map((slot) => {
      const key = this.prodKey(slot, rec), v = this.variantFor(key, a);
      if (!v || !v.id) return null;
      value += v.price || 0;
      const item = { id: v.id, quantity: 1, properties: { _quiz_archetype: arch } };
      if (sub && v.sp) { item.selling_plan = v.sp; subbed++; }
      return item;
    }).filter(Boolean);
    if (!items.length) { window.location.href = this.cfg.kitUrl || '/pages/subscribe'; return; }
    // asked to subscribe but no plan is configured -> hand off to the proven subscribe flow rather than fake a one-time add
    if (sub && !subbed) { window.location.href = this.cfg.kitUrl || '/pages/subscribe'; return; }
    const label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = (this.cfg.i18n && this.cfg.i18n.adding) || 'Adding…'; }
    try {
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      if (!res.ok) throw new Error('add failed');
      this.#track('Quiz Add to Cart', { 'Dog Archetype': arch, 'Quiz Result': rec.isKit ? 'Cleanup Kit' : rec.primary, 'Item Count': items.length, '$value': value / 100, 'Purchase Type': sub ? 'Subscription' : 'One-time' });
      let cart = null;
      try { cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json(); } catch (_) {}
      document.dispatchEvent(new CartUpdateEvent(cart || {}, 'pogi-quiz', { source: 'pogi-quiz', itemCount: cart ? cart.item_count : undefined }));
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.showDialog === 'function') { drawer.showDialog(); if (btn) { btn.disabled = false; btn.textContent = label; } }
      else window.location.href = '/cart';
    } catch (e) { if (btn) { btn.disabled = false; btn.textContent = 'Try again'; } }
  }

  /* keep the typed name in state on every keystroke so a re-render (gender tap)
     never wipes it, and it shows back up on the result card */
  #onInput = (e) => {
    const inp = e.target.closest && e.target.closest('.pogi-quiz__nameinput'); if (!inp) return;
    this.answers.name = inp.value;
    const wink = this.querySelector('.pogi-quiz__namewink'); if (wink) wink.textContent = this.#nameWink(inp.value);
  };

  #focusName() {
    // don't auto-pop the keyboard on touch devices; let them tap the field
    try { if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return; } catch (_) {}
    const inp = this.querySelector('.pogi-quiz__nameinput');
    if (inp) { try { inp.focus(); const n = inp.value.length; inp.setSelectionRange(n, n); } catch (_) {} }
  }

  /* re-anchor every screen to the same spot (just under the sticky header) so the
     page never lands somewhere random as content height changes between screens */
  #scrollTop() {
    try {
      const sec = this.closest('.pogi-quiz') || this;
      let off = 8;
      document.querySelectorAll('.header-wrapper, #header-component, .section-header, header').forEach((h) => {
        const p = getComputedStyle(h).position; const r = h.getBoundingClientRect();
        if ((p === 'sticky' || p === 'fixed') && r.height && r.top < 60) off = Math.max(off, r.height + 6);
      });
      const y = window.pageYOffset + sec.getBoundingClientRect().top - off;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    } catch (_) {}
  }

  /* ---------- analytics (Klaviyo onsite custom events) ---------- */
  #track(event, props) { try { if (window.klaviyo && window.klaviyo.push) window.klaviyo.push(['track', event, props || {}]); } catch (_) {} }
  #trackStart() { if (this._trackedStart) return; this._trackedStart = true; this.#track('Started Quiz', {}); }

  #advance() {
    if (this.qi < this.STEPS.length - 1) { this.qi += 1; this.phase = 'q'; this.render(); this.#scrollTop(); this.#focusName(); }
    else { this.#reveal(); }
  }
  #reveal() {
    this.phase = 'reveal';
    this._revMsgs = ['Measuring floof…', 'Calculating chaos…', 'Checking for mud…', `Sniffing out ${this.dnPoss()} routine…`];
    this._revI = 0;
    this.render();
    this.#scrollTop();
    clearInterval(this._revInt);
    this._revInt = setInterval(() => {
      this._revI = (this._revI + 1) % this._revMsgs.length;
      const el = this.querySelector('.pogi-quiz__reveal-txt');
      if (el) { el.textContent = this._revMsgs[this._revI]; el.style.animation = 'none'; void el.offsetWidth; el.style.animation = ''; }
    }, 540);
    clearTimeout(this._t); clearTimeout(this._shakeT);
    this._shakeT = setTimeout(() => this.#shakeOff(), 1350);
    this._t = setTimeout(() => { clearInterval(this._revInt); this.phase = 'result'; this.render(); this.#scrollTop(); }, 2450);
  }
  /* the payoff: near the end of the reveal the filthy pup shakes, mud flings off as
     droplets, then he lands sparkling clean on the result card. */
  #shakeOff() {
    const stage = this.querySelector('.pogi-quiz__reveal');
    if (!stage) return;
    this.#beep(190, 0.09, 'sawtooth', 0, 0.05); this.#beep(150, 0.11, 'sawtooth', 0.09, 0.05);
    if (navigator.vibrate) { try { navigator.vibrate([0, 30, 40, 30]); } catch (_) {} }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    stage.classList.add('is-shaking');
    const av = stage.querySelector('.pogi-quiz__guide');
    if (!av) return;
    const fly = document.createElement('div'); fly.className = 'pogi-quiz__mudfly'; fly.setAttribute('aria-hidden', 'true');
    let html = '';
    for (let i = 0; i < 12; i++) {
      const ang = (i / 12) * Math.PI * 2 + (i % 2 ? 0.3 : -0.2);
      const dist = 58 + (i % 4) * 20;
      const dx = Math.cos(ang) * dist, dy = Math.sin(ang) * dist * 0.8 + 8, sz = 6 + (i % 3) * 3;
      html += `<span style="--dx:${dx.toFixed(0)}px;--dy:${dy.toFixed(0)}px;width:${sz}px;height:${sz}px;animation-delay:${(i % 5) * 18}ms"></span>`;
    }
    fly.innerHTML = html; av.appendChild(fly);
    setTimeout(() => { try { fly.remove(); } catch (_) {} }, 950);
  }

  /* ---------- result-page easter eggs ---------- */
  #pop(host, emoji, delay) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const s = document.createElement('span'); s.className = 'pogi-quiz__pop'; s.textContent = emoji; s.setAttribute('aria-hidden', 'true');
    s.style.left = (38 + Math.random() * 24) + '%';
    if (delay) s.style.animationDelay = delay + 'ms';
    host.appendChild(s);
    setTimeout(() => { try { s.remove(); } catch (_) {} }, 1250 + (delay || 0));
  }
  // tap the pup for a little pop; 5 quick taps = a boop celebration
  #boop(pup) {
    pup.classList.remove('is-spin'); void pup.offsetWidth; pup.classList.add('is-spin');
    setTimeout(() => pup.classList.remove('is-spin'), 660);
    this.#beep(880, 0.08, 'square', 0, 0.05); this.#beep(1245, 0.1, 'square', 0.06, 0.04);
    if (navigator.vibrate) { try { navigator.vibrate(10); } catch (_) {} }
    this.#pop(pup, Math.random() < 0.5 ? '❤️' : '🐾');
    this._boops = (this._boops || 0) + 1;
    clearTimeout(this._boopT); this._boopT = setTimeout(() => { this._boops = 0; }, 1300);
    if (this._boops >= 5) {
      this._boops = 0;
      this.#toast('boop! 🐽');
      this.#beep(523, 0.07, 'square', 0, 0.05); this.#beep(784, 0.09, 'square', 0.07, 0.05); this.#beep(1046, 0.12, 'triangle', 0.15, 0.05);
      for (let i = 0; i < 8; i++) this.#pop(pup, '❤️', i * 45);
      if (navigator.vibrate) { try { navigator.vibrate([0, 30, 30, 30]); } catch (_) {} }
    }
  }
  #secretRain(emoji, n) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const host = this.querySelector('.pogi-quiz__result') || this;
    const wrap = document.createElement('div'); wrap.className = 'pogi-quiz__rain'; wrap.setAttribute('aria-hidden', 'true');
    let html = '';
    for (let i = 0; i < n; i++) { const l = (Math.random() * 100).toFixed(1), d = Math.floor(Math.random() * 500), dur = 1500 + Math.floor(Math.random() * 900); html += `<span style="left:${l}%;animation-delay:${d}ms;animation-duration:${dur}ms">${emoji}</span>`; }
    wrap.innerHTML = html; host.appendChild(wrap);
    setTimeout(() => { try { wrap.remove(); } catch (_) {} }, 2800);
  }
  #woof() {
    this.#beep(320, 0.1, 'square', 0, 0.06); this.#beep(180, 0.16, 'square', 0.08, 0.06);
    if (navigator.vibrate) { try { navigator.vibrate([0, 40, 30, 50]); } catch (_) {} }
    if (!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      const host = this.querySelector('.pogi-quiz__card') || this.querySelector('.pogi-quiz__result');
      if (host) { const b = document.createElement('div'); b.className = 'pogi-quiz__woof'; b.textContent = 'WOOF!'; b.setAttribute('aria-hidden', 'true'); host.appendChild(b); setTimeout(() => { try { b.remove(); } catch (_) {} }, 1100); }
    }
    const pup = this.querySelector('.pogi-quiz__avatar'); if (pup) { pup.classList.remove('is-spin'); void pup.offsetWidth; pup.classList.add('is-spin'); setTimeout(() => pup.classList.remove('is-spin'), 660); }
  }
  // type a secret word on the result: treat / pogi / woof
  #onKey = (e) => {
    if (this.phase !== 'result') return;
    const t = e.target; if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    const k = (e.key || '').toLowerCase();
    if (k.length !== 1 || k < 'a' || k > 'z') return;
    this._kbuf = ((this._kbuf || '') + k).slice(-6);
    if (this._kbuf.endsWith('treat')) { this._kbuf = ''; this.#secretRain('🦴', 16); this.#toast('Treats incoming! 🦴'); this.#sfxWin(); }
    else if (this._kbuf.endsWith('woof')) { this._kbuf = ''; this.#woof(); }
    else if (this._kbuf.endsWith('pogi')) { this._kbuf = ''; this.#secretRain('✨', 18); this.#toast('🐾 You found Pogi!'); this.#sfxWin(); }
  };
  #choose(id, value) {
    this.answers = { ...this.answers, [id]: value };
    if (navigator.vibrate) { try { navigator.vibrate(12); } catch (_) {} }
    this.#sfxSelect();
    this.reaction = (this.REACTIONS[id] || {})[value] || '';
    this.render();
    this.#scrollTop();
    clearTimeout(this._t);
    this._t = setTimeout(() => { this.reaction = ''; this.#advance(); }, this.reaction ? 1100 : 200);
  }
  #back() {
    this.reaction = ''; clearInterval(this._revInt); clearTimeout(this._t); clearTimeout(this._shakeT); this._celebrated = false;
    if (this.phase === 'result' || this.phase === 'reveal') { this.phase = 'q'; this.qi = this.STEPS.length - 1; }
    else if (this.qi > 0) this.qi -= 1;
    else this.phase = 'intro';
    this.render();
    this.#scrollTop();
  }

  #onSubmit = (e) => {
    const nameForm = e.target.closest('[data-q-name]');
    if (nameForm) { e.preventDefault(); clearTimeout(this._t); this.answers = { ...this.answers, name: (new FormData(nameForm).get('dogname') || '').trim() }; this.#advance(); return; }
    const form = e.target.closest('[data-q-capture]');
    if (!form) return;
    e.preventDefault();
    const email = (new FormData(form).get('email') || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    const a = this.answers, rec = this.recommend(a);
    // human-readable labels so the Klaviyo profile reads like a real dog bio
    const L = {
      gender: { girl: 'Girl', boy: 'Boy' },
      stage: { puppy: 'Puppy', adult: 'Adult', senior: 'Senior', pack: 'Multiple dogs' },
      size: { small: 'Small', medium: 'Medium', large: 'Large', giant: 'Giant' },
      skin: { sensitive: 'Sensitive or itchy', fresh: 'Normal skin', unsure: 'Not sure' },
      coat: { muddy: 'Muddy paws', coat: 'Dirty coat and belly', folds: 'Wrinkles and folds', clean: 'Pretty clean' },
      bags: { leakproof: 'Wants strong, leak-proof', compostable: 'Wants compostable', runout: 'Wants to never run out' },
      indoor: { training: 'Training or accidents', apartment: 'Apartment, no yard', weather: 'Bad-weather backup', none: 'None' },
      travel: { home: 'Around home', hikes: 'Trails and hikes', roadtrips: 'Road trips and travel', everywhere: 'Everywhere' },
    };
    const lab = (k) => (L[k] || {})[a[k]] || '';
    const recSlots = rec.isKit ? rec.strong : [rec.primary];
    const recNames = recSlots.map((s) => (this.PRODUCTS[this.prodKey(s, rec)] || {}).name || s).join(', ');
    const props = {
      'Signup Source': 'Quiz',
      'Dog Name': a.name || '',
      'Dog Gender': lab('gender'),
      'Dog Stage': lab('stage'),
      'Dog Size': lab('size'),
      'Skin': lab('skin'),
      'After-Walk State': lab('coat'),
      'Bag Gripe': lab('bags'),
      'Indoor Potty': lab('indoor'),
      'Mischief Spot': lab('travel'),
      'Dog Archetype': (this.ARCHETYPES[this.archetypeKey(a)] || {}).name || '',
      'Quiz Result': rec.isKit ? 'Cleanup Kit' : recNames,
      'Recommended Products': recNames
    };
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + this.COMPANY, {
      method: 'POST', headers: { 'revision': '2024-10-15', 'content-type': 'application/json' },
      body: JSON.stringify({ data: { type: 'subscription', attributes: {
        custom_source: 'Quiz', profile: { data: { type: 'profile', attributes: { email: email, properties: props } } }
      }, relationships: { list: { data: { type: 'list', id: this.LIST } } } } })
    }).catch(() => {});
    if (window.klaviyo) { try { window.klaviyo.push(['identify', { '$email': email }]); } catch (_) {} }
    this.cap = { sent: true };
    this.render();
  };

  /* ---------- icons ---------- */
  icon(name, size) {
    const P = {
      baby: '<path d="M9 12h.01"/><path d="M15 12h.01"/><path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5"/><path d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5s-.9 2.5-2 2.5c-.8 0-1.5-.4-1.5-1"/>',
      paw: '<ellipse cx="5.5" cy="10" rx="1.7" ry="2.3"/><ellipse cx="9.6" cy="6.6" rx="1.8" ry="2.5"/><ellipse cx="14.4" cy="6.6" rx="1.8" ry="2.5"/><ellipse cx="18.5" cy="10" rx="1.7" ry="2.3"/><path d="M12 12.2c1.9 0 3.4 1.1 4.3 2.6 1 1.7 1.1 3.9-.2 5.1-1 .9-2.5.5-4.1-.3-1.6.8-3.1 1.2-4.1.3-1.3-1.2-1.2-3.4-.2-5.1.9-1.5 2.4-2.6 4.3-2.6z"/>',
      heart: '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
      users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
      sparkles: '<path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z"/>',
      leaf: '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>',
      house: '<path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>',
      layers: '<path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"/><path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"/><path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17"/>',
      shuffle: '<path d="m18 14 4 4-4 4"/><path d="m18 2 4 4-4 4"/><path d="M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6a4 4 0 0 1 3.3-1.7H22"/><path d="M2 6h1.972a4 4 0 0 1 3.6 2.2"/><path d="M22 18h-6.041a4 4 0 0 1-3.3-1.8l-.359-.45"/>',
      wind: '<path d="M12.8 19.6A2 2 0 1 0 14 16H2"/><path d="M17.5 8a2.5 2.5 0 1 1 2 4H2"/><path d="M9.8 4.4A2 2 0 1 1 11 8H2"/>',
      droplets: '<path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 4.7 7 2.75c-.29 1.95-1.14 3.34-2.29 4.31S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/><path d="M12.56 6.6A11 11 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a7 7 0 0 1-11.91 4.97"/>',
      check: '<path d="M20 6 9 17l-5-5"/>',
      'arrow-left': '<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>',
      mail: '<rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>',
    };
    return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${P[name] || ''}</svg>`;
  }
  esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  money(c) { const n = Math.round(c || 0) / 100; return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  /* generated pup avatar - assembles from the answers so far (partial-safe).
     Floppy ears hang from behind the head so it reads as a DOG, not a bear.
     No archetype accessory until at least one trait is known (clean base pup). */
  /* how grubby the pup looks right now: clean at the name step, muddier with every
     question, peak-filthy on the reveal, then sparkling clean on the result card. */
  #dirtNow() {
    if (this.phase === 'reveal') return 1;
    if (this.phase === 'result') return 0;
    if (this.phase === 'q') return Math.min(1, this.qi / Math.max(1, this.STEPS.length - 1));
    return 0;
  }

  /* deterministic organic blob path (stable across re-renders, no Math.random so the
     splats never jump between questions). Vertex radii jitter via a seeded sin-hash,
     then we smooth through the midpoints for a soft, mud-like outline. */
  #blobPath(cx, cy, r, seed) {
    const N = 9;
    const rnd = (n) => { const x = Math.sin(seed * 12.9898 + n * 78.233) * 43758.5453; return x - Math.floor(x); };
    const pts = [];
    for (let j = 0; j < N; j++) {
      const ang = (j / N) * Math.PI * 2;
      const rr = r * (0.6 + rnd(j) * 0.6);
      pts.push([cx + Math.cos(ang) * rr, cy + Math.sin(ang) * rr * 0.86]);
    }
    const mid = (j) => { const a = pts[j], b = pts[(j + 1) % N]; return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]; };
    const m = mid(N - 1);
    let d = `M${m[0].toFixed(1)},${m[1].toFixed(1)}`;
    for (let j = 0; j < N; j++) { const c = pts[j], e = mid(j); d += `Q${c[0].toFixed(1)},${c[1].toFixed(1)} ${e[0].toFixed(1)},${e[1].toFixed(1)}`; }
    return d + 'Z';
  }

  /* one mud splat: jagged blob + darker core + a small highlight + a few flung
     droplets, so it reads as kicked-up mud instead of a flat blotch. */
  #mudSplat(s, i) {
    const seed = (i + 1) * 1.7, r = (s.rx + s.ry) / 2;
    const rnd = (n) => { const x = Math.sin(seed * 3.3 + n * 51.7) * 43758.5453; return x - Math.floor(x); };
    let drops = '';
    const nd = 3 + Math.floor(rnd(0) * 2);
    for (let k = 0; k < nd; k++) {
      const ang = rnd(k + 1) * Math.PI * 2, dist = r * (1.05 + rnd(k + 7) * 0.9), dr = r * (0.1 + rnd(k + 13) * 0.18);
      drops += `<circle cx="${(s.cx + Math.cos(ang) * dist).toFixed(1)}" cy="${(s.cy + Math.sin(ang) * dist * 0.85).toFixed(1)}" r="${dr.toFixed(1)}" fill="#5A3F22"/>`;
    }
    return `<path d="${this.#blobPath(s.cx, s.cy, r * 1.05, seed)}" fill="#6E4F2C"/>`
      + `<path d="${this.#blobPath(s.cx + 0.5, s.cy + 0.6, r * 0.56, seed + 4.1)}" fill="#4F3619" opacity=".6"/>`
      + `<path d="${this.#blobPath(s.cx - 0.9, s.cy - 0.9, r * 0.28, seed + 8.6)}" fill="#8A6638" opacity=".5"/>`
      + drops;
  }

  avatarSVG(a, px, opts) {
    px = px || 132;
    const sparkle = !!(opts && opts.sparkle);
    const dirtLevel = (opts && typeof opts.dirt === 'number') ? opts.dirt : this.#dirtNow();
    const hasTraits = !!(a.coat || a.stage || a.indoor || a.bags);
    const k = this.archetypeKey(a);
    const ring = ({ wrinkle: '#E8B04B', senior: '#9AA77E', tornado: '#F26A3D', mud: '#7C8A4E', pack: '#5F6E3A', trail: '#6E8FA0', giant: '#8E7CA6', city: '#7C8696', sensitive: '#E0A6B8', eco: '#6E8B3D', couch: '#C99A6B' })[k] || '#7C8A4E';
    const coat = '#F0DCB2', coatDk = '#CDA76E', snout = '#FBF1DD', dark = '#3A2F25';
    const puppy = a.stage === 'puppy', old = a.stage === 'senior';
    const scale = ({ small: 0.84, medium: 1, large: 1.12, giant: 1.22 })[a.size] || 1;
    let eyes;
    if (old) eyes = `<g stroke="${dark}" stroke-width="1.6" fill="none"><circle cx="49" cy="58" r="6.6"/><circle cx="71" cy="58" r="6.6"/><line x1="55.6" y1="58" x2="64.4" y2="58"/></g><circle cx="49" cy="58" r="2.5" fill="${dark}"/><circle cx="71" cy="58" r="2.5" fill="${dark}"/>`;
    else { const r = puppy ? 5.8 : 4.7; eyes = `<circle cx="49" cy="58" r="${r}" fill="${dark}"/><circle cx="71" cy="58" r="${r}" fill="${dark}"/><circle cx="50.9" cy="56" r="1.7" fill="#fff"/><circle cx="72.9" cy="56" r="1.7" fill="#fff"/>`; }
    // ---- archetype TOPPER (head-top flourish only; mud/bandana/scarf now come from
    //      individual answers below so the dog reads as a live build-a-dog) ----
    let acc = '';
    if (hasTraits) {
      if (k === 'wrinkle') acc = `<path d="M45 28 L51 37 L60 26 L69 37 L75 28 L73 40 L47 40 Z" fill="#E8B04B" stroke="#C98A1E" stroke-width="1.2" stroke-linejoin="round"/><circle cx="60" cy="25" r="2.7" fill="#F26A3D"/>`;
      else if (k === 'tornado') acc = `<path d="M60 13 L73 38 L47 38 Z" fill="#F26A3D"/><path d="M47 38 q13 5 26 0" fill="none" stroke="#fff" stroke-width="2.2" opacity=".75"/><circle cx="60" cy="12" r="3.3" fill="#E8B04B"/>`;
      else if (k === 'couch') acc = `<text x="84" y="40" font-size="13" fill="${dark}" font-family="Arial" opacity=".7">z</text><text x="91" y="30" font-size="9" fill="${dark}" font-family="Arial" opacity=".5">z</text>`;
    }

    // ---- answer-driven cosmetics, each pinned to its own zone so they never collide ----
    const wantsBandana = a.travel === 'hikes' || a.travel === 'everywhere';
    const wantsShades = !old && (a.travel === 'roadtrips' || a.travel === 'everywhere');
    // senior: greyed muzzle (over the snout, under eyes/nose)
    const greyMuzzle = old ? `<ellipse cx="60" cy="69" rx="17" ry="13.5" fill="#EDE6D6" opacity=".6"/><ellipse cx="49" cy="60" rx="6" ry="4" fill="#EDE6D6" opacity=".4"/><ellipse cx="71" cy="60" rx="6" ry="4" fill="#EDE6D6" opacity=".4"/>` : '';
    // folds: face creases (an alternative to mud)
    const creases = a.coat === 'folds' ? `<g stroke="${coatDk}" stroke-width="1.5" fill="none" opacity=".75" stroke-linecap="round"><path d="M47 47 q13 -4 26 0"/><path d="M49 52 q11 -3 22 0"/><path d="M50 79 q10 4 20 0"/></g>` : '';
    // road-trip shades (win the eye zone; senior keeps spectacles)
    const eyewear = wantsShades ? `<g><path d="M40 55 h40" stroke="#1F1F1F" stroke-width="2.4"/><rect x="40" y="53" width="17" height="11" rx="4" fill="#23262B"/><rect x="63" y="53" width="17" height="11" rx="4" fill="#23262B"/><path d="M43 56 l5 0 M66 56 l5 0" stroke="#fff" stroke-width="2" opacity=".5" stroke-linecap="round"/></g>` : '';
    // hikes/everywhere: bandana around the neck
    const bandana = wantsBandana ? `<g><rect x="42" y="82" width="36" height="5.5" rx="2.7" fill="#9E3B22"/><path d="M45 85 L75 85 L60 104 Z" fill="#C0492B"/><path d="M51 90 l3 3 M60 90 l0 4 M69 90 l-3 3" stroke="#fff" stroke-width="1.5" opacity=".55" stroke-linecap="round"/></g>` : '';
    // fresh scent on a GIRL: a little flower by the right ear (a feminine accent)
    const flower = (a.skin === 'fresh' && a.gender === 'girl') ? `<g transform="translate(83 41)">${[0, 72, 144, 216, 288].map(function (d) { return `<circle cx="${(Math.cos(d * Math.PI / 180) * 3.4).toFixed(1)}" cy="${(Math.sin(d * Math.PI / 180) * 3.4).toFixed(1)}" r="2.4" fill="#F2C0D2"/>`; }).join('')}<circle cx="0" cy="0" r="2.2" fill="#F2D27A"/></g>` : '';
    // compostable bags: a small leaf BADGE pinned on the ring (rendered outside the
    // scaled dog group below, so it's a fixed emblem, not an earring on his head)
    const leaf = a.bags === 'compostable' ? `<g transform="translate(95 95)"><circle r="11" fill="#F4ECDA" stroke="#6E8B3D" stroke-width="1.6"/><g transform="translate(-4 2) scale(.95) rotate(6)"><path d="M0 0 q6 -3 9 -9 q-6 1 -9 4 q-2 2 0 5 Z" fill="#6E8B3D"/><path d="M1 -1 q4 -3 7 -7" stroke="#4E6A28" stroke-width="1" fill="none"/></g></g>` : '';
    // a whole pack: a 2nd mini-pup peeking from behind, lower-left
    const packPup = a.stage === 'pack' ? `<g transform="translate(24 86) scale(.52)"><ellipse cx="-14" cy="20" rx="7" ry="14" fill="${coatDk}" transform="rotate(18 -14 20)"/><ellipse cx="0" cy="0" rx="26" ry="23" fill="#E7CFA0"/><circle cx="-9" cy="2" r="3.4" fill="${dark}"/><circle cx="9" cy="2" r="3.4" fill="${dark}"/><ellipse cx="0" cy="11" rx="4" ry="3" fill="${dark}"/></g>` : '';

    const gender = a.gender === 'girl'
      ? `<g transform="translate(30 42)"><path d="M0 0 L-12 -8 L-12 9 Z" fill="#F26A3D"/><path d="M0 0 L12 -8 L12 9 Z" fill="#F26A3D"/><circle cx="0" cy="0.5" r="3.6" fill="#D9572C"/></g>`
      : (a.gender === 'boy' && !wantsBandana)
      ? `<g transform="translate(60 92)"><path d="M0 0 L-14 -8 L-14 8 Z" fill="#F26A3D"/><path d="M0 0 L14 -8 L14 8 Z" fill="#F26A3D"/><rect x="-4.2" y="-4.2" width="8.4" height="8.4" rx="2" fill="#D9572C"/></g>`
      : '';
    // progressive mud: fixed splat positions revealed one-by-one as the quiz goes on,
    // so it ramps deterministically (no flicker) instead of randomizing each render.
    let dirt = '';
    const SPOTS = [
      { cx: 48, cy: 50, rx: 5.2, ry: 3.8, rot: -20, op: 0.5 },
      { cx: 73, cy: 47, rx: 4.8, ry: 3.4, rot: 14, op: 0.5 },
      { cx: 60, cy: 78, rx: 5.0, ry: 3.2, rot: 0, op: 0.5 },
      { cx: 36, cy: 58, rx: 3.8, ry: 5.0, rot: 8, op: 0.45 },
      { cx: 84, cy: 58, rx: 3.8, ry: 5.0, rot: -8, op: 0.45 },
      { cx: 52, cy: 68, rx: 3.6, ry: 2.8, rot: -12, op: 0.42 },
      { cx: 70, cy: 70, rx: 4.0, ry: 3.0, rot: 16, op: 0.45 },
      { cx: 60, cy: 40, rx: 4.2, ry: 2.8, rot: 0, op: 0.4 },
    ];
    // the coat answer shapes how grubby he gets: a clean dog resists it, a muddy/grubby
    // one earns extra. dirtLevel<=0 (intro + result card) forces a spotless pup.
    let effDirt = dirtLevel;
    if (dirtLevel > 0) {
      if (a.coat === 'clean') effDirt = dirtLevel * 0.35;
      else if (a.coat === 'coat') effDirt = Math.min(1, dirtLevel + 0.2);
      else if (a.coat === 'muddy') effDirt = Math.min(1, dirtLevel + 0.12);
    }
    const nDirt = dirtLevel > 0 ? Math.max(0, Math.min(SPOTS.length, Math.round(effDirt * SPOTS.length))) : 0;
    for (let i = 0; i < nDirt; i++) {
      const s = SPOTS[i];
      // only the newest splat animates in; earlier ones render already-settled so the
      // whole face doesn't re-fade on every question re-render
      const fresh = i === nDirt - 1;
      dirt += `<g class="pogi-quiz__dirt${fresh ? ' is-fresh' : ''}" style="--o:${s.op}">${this.#mudSplat(s, i)}</g>`;
    }
    // victory sparkles: clean-dog twinkles. Each star is positioned by a wrapping <g>
    // (translate stays in the attribute) so the CSS scale/opacity animation on the
    // inner <path> doesn't clobber its placement.
    let spark = '';
    if (sparkle) {
      const STARS = [[22, 30, 1, 0], [96, 34, 0.85, 180], [26, 92, 0.8, 320], [97, 88, 0.95, 460], [60, 15, 0.7, 620], [12, 58, 0.62, 760]];
      spark = `<g class="pogi-quiz__spark" aria-hidden="true">`
        + STARS.map(function (st) { return `<g transform="translate(${st[0]} ${st[1]}) scale(${st[2]})"><path class="pogi-quiz__spark-star" style="animation-delay:${st[3]}ms" d="M0,-7 C.8,-2 2,-.8 7,0 C2,.8 .8,2 0,7 C-.8,2 -2,.8 -7,0 C-2,-.8 -.8,-2 0,-7 Z" fill="#F2D27A"/></g>`; }).join('')
        + `</g>`;
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" class="pogi-quiz__avsvg" viewBox="0 0 120 120" width="${px}" height="${px}" role="img" aria-label="${this.esc(this.dn())} the dog">
      <circle cx="60" cy="60" r="57" fill="${ring}" opacity=".16"/>
      <circle cx="60" cy="60" r="57" fill="none" stroke="${ring}" stroke-width="3"/>
      <g transform="translate(60 60) scale(${scale}) translate(-60 -60)">
        ${packPup}
        <ellipse cx="33" cy="62" rx="8.5" ry="28" fill="${coatDk}" transform="rotate(9 33 62)"/>
        <ellipse cx="87" cy="62" rx="8.5" ry="28" fill="${coatDk}" transform="rotate(-9 87 62)"/>
        <ellipse cx="34" cy="54" rx="4.4" ry="17" fill="#EFC9A0" opacity=".5" transform="rotate(9 34 54)"/>
        <ellipse cx="86" cy="54" rx="4.4" ry="17" fill="#EFC9A0" opacity=".5" transform="rotate(-9 86 54)"/>
        <ellipse cx="60" cy="58" rx="30" ry="27" fill="${coat}"/>
        <ellipse cx="60" cy="70" rx="16" ry="13" fill="${snout}"/>
        ${greyMuzzle}
        <ellipse cx="43" cy="65" rx="5" ry="3.2" fill="#F26A3D" opacity=".26"/>
        <ellipse cx="77" cy="65" rx="5" ry="3.2" fill="#F26A3D" opacity=".26"/>
        ${eyes}
        <ellipse cx="60" cy="64" rx="4.7" ry="3.4" fill="${dark}"/>
        <path d="M60 67.4 L60 71" stroke="${dark}" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M60 71 q-5 4.5 -9 1.4 M60 71 q5 4.5 9 1.4" fill="none" stroke="${dark}" stroke-width="1.6" stroke-linecap="round"/>
        ${puppy ? `<path d="M56.5 74 q3.5 6 7 0 Z" fill="#E58B6B"/>` : ''}
        ${creases}${dirt}${eyewear}${bandana}${flower}${acc}${gender}
      </g>
      ${spark}${leaf}
    </svg>`;
  }

  /* "POGI'S CERTIFIED · [archetype]" rubber-stamp seal. color defaults to currentColor
     (CSS drives it on the page); pass an explicit color when rasterizing for the share
     card, where there's no CSS context. Archetype balances onto up to 2 lines. */
  sealSVG(name, color) {
    color = color || 'currentColor';
    const t = String(name || 'Pogi').replace(/^the\s+/i, '').toUpperCase();
    const words = t.split(' ');
    let l1 = t, l2 = '';
    if (words.length > 1) {
      let cut = 1, diff = 1e9;
      for (let i = 1; i < words.length; i++) { const a = words.slice(0, i).join(' '), b = words.slice(i).join(' '), d = Math.abs(a.length - b.length); if (d < diff) { diff = d; cut = i; } }
      l1 = words.slice(0, cut).join(' '); l2 = words.slice(cut).join(' ');
    }
    const maxlen = Math.max(l1.length, l2.length);
    const fs = maxlen > 11 ? 9 : maxlen > 8 ? 11 : 12.5;
    const ty = l2 ? 55 : 59;
    const paw = `<g transform="translate(50 34)"><ellipse cx="-6.5" cy="-1.5" rx="2.4" ry="3.2"/><ellipse cx="-2.2" cy="-4.6" rx="2.4" ry="3.2"/><ellipse cx="2.2" cy="-4.6" rx="2.4" ry="3.2"/><ellipse cx="6.5" cy="-1.5" rx="2.4" ry="3.2"/><path d="M0 0 c4.2 0 6.6 3 6.6 6.2 0 3.2 -3.2 4.2 -6.6 3.2 -3.4 1 -6.6 0 -6.6 -3.2 0 -3.2 2.4 -6.2 6.6 -6.2 z"/></g>`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" class="pogi-quiz__seal-svg" aria-hidden="true" fill="${color}" font-family="'Poppins','Segoe UI',Roboto,sans-serif">
      <circle cx="50" cy="50" r="47" fill="none" stroke="${color}" stroke-width="2.4"/>
      <circle cx="50" cy="50" r="41.5" fill="none" stroke="${color}" stroke-width="1" stroke-dasharray="1.5 2.6"/>
      <text x="50" y="20" text-anchor="middle" font-size="7.3" font-weight="700" letter-spacing="1.3">POGI'S CERTIFIED</text>
      ${paw}
      <text x="50" y="${ty}" text-anchor="middle" font-size="${fs}" font-weight="800" letter-spacing=".3">${this.esc(l1)}</text>
      ${l2 ? `<text x="50" y="${ty + fs}" text-anchor="middle" font-size="${fs}" font-weight="800" letter-spacing=".3">${this.esc(l2)}</text>` : ''}
      <text x="50" y="90" text-anchor="middle" font-size="7" letter-spacing="2.5">★ ★ ★</text>
    </svg>`;
  }

  /* ---------- render ---------- */
  render() {
    if (this.phase === 'intro') this.mount.innerHTML = this.#intro();
    else if (this.phase === 'q') this.mount.innerHTML = this.#question();
    else if (this.phase === 'reveal') this.mount.innerHTML = this.#revealView();
    else { this.mount.innerHTML = this.#result(); this.#celebrate(); }
  }

  #intro() {
    const c = this.cfg;
    return `<div class="pogi-quiz__intro">
      ${c.introImg ? `<img class="pogi-quiz__intro-img" src="${this.esc(c.introImg)}" alt="">` : ''}
      <p class="pogi-quiz__eyebrow">${this.esc(c.introEyebrow || 'Find your routine')}</p>
      <h1 class="pogi-quiz__intro-title">${this.esc(c.introTitle || 'Build your dog’s perfect cleanup routine.')}</h1>
      <p class="pogi-quiz__intro-body">${this.esc(c.introBody || 'A few quick questions and we’ll reveal your dog’s type plus the exact Pogi’s routine for their messes, skin, and size.')}</p>
      <button type="button" class="pogi-cta pogi-cta--primary" data-q="start">${this.esc(c.startLabel || 'Start the quiz')}</button>
      <p class="pogi-quiz__intro-note">${this.esc(c.introNote || 'Takes about a minute')}</p>
    </div>`;
  }

  #qhead(total) {
    const bars = Array.from({ length: total }, (_, k) => `<span class="pogi-quiz__bar${k <= this.qi ? ' is-on' : ''}"></span>`).join('');
    return `<div class="pogi-quiz__bars">${bars}</div>
      <div class="pogi-quiz__qhead">
        <button type="button" class="pogi-quiz__back" data-q="back">${this.icon('arrow-left', 15)} Back</button>
        <span class="pogi-quiz__count">${this.qi + 1} of ${total}</span>
      </div>`;
  }

  #question() {
    const steps = this.STEPS, step = steps[this.qi], total = steps.length;
    if (this.reaction) {
      return `<div class="pogi-quiz__q pogi-quiz__reacting">
        ${this.#qhead(total)}
        <div class="pogi-quiz__react">
          <div class="pogi-quiz__guide">${this.avatarSVG(this.answers, 108)}</div>
          <p class="pogi-quiz__react-quip" aria-live="polite">${this.esc(this.reaction)}</p>
        </div>
      </div>`;
    }
    if (step.type === 'text') {
      const g = this.answers.gender || '';
      const gb = (v, lbl) => `<button type="button" class="pogi-quiz__gbtn${g === v ? ' is-sel' : ''}" data-q="gender" data-value="${v}" aria-pressed="${g === v}">${lbl}</button>`;
      return `<div class="pogi-quiz__q">
        ${this.#qhead(total)}
        <div class="pogi-quiz__guide pogi-quiz__guide--name">${this.avatarSVG(this.answers, 92)}</div>
        <div class="pogi-quiz__perks">
          <span class="pogi-quiz__perk">🐾 Your dog’s type</span>
          <span class="pogi-quiz__perk">🎁 Custom routine</span>
          <span class="pogi-quiz__perk">💸 15% off</span>
          <span class="pogi-quiz__perks-time">About a minute · 8 quick taps</span>
        </div>
        <h2 class="pogi-quiz__qtitle">${this.esc(step.q)}</h2>
        ${step.sub ? `<p class="pogi-quiz__qsub">${this.esc(step.sub)}</p>` : ''}
        <form class="pogi-quiz__nameform" data-q-name autocomplete="off">
          <input class="pogi-quiz__nameinput" name="dogname" type="text" value="${this.esc(this.answers.name || '')}" placeholder="${this.esc(step.placeholder)}" autocomplete="off" autocorrect="off" autocapitalize="words" spellcheck="false" inputmode="text" enterkeyhint="next" maxlength="40" data-1p-ignore data-lpignore="true" data-form-type="other">
          <p class="pogi-quiz__namewink" aria-live="polite">${this.esc(this.#nameWink(this.answers.name || ''))}</p>
          <p class="pogi-quiz__namehint">Pick one to keep going</p>
          <div class="pogi-quiz__gender" role="group" aria-label="Dog gender">${gb('girl', 'Girl')}${gb('boy', 'Boy')}${gb('surprise', 'Surprise me')}</div>
        </form>
        <button type="button" class="pogi-quiz__skip" data-q="name-skip">Skip this</button>
      </div>`;
    }
    const opts = step.options.map((o) => {
      const sel = this.answers[step.id] === o.value;
      return `<button type="button" class="pogi-quiz__opt${sel ? ' is-sel' : ''}" data-q="opt" data-step="${step.id}" data-value="${o.value}" aria-pressed="${sel}">
        <span class="pogi-quiz__opt-ic">${o.emoji ? `<span class="pogi-quiz__opt-emoji" aria-hidden="true">${o.emoji}</span>` : this.icon(o.icon, 22)}</span>
        <span class="pogi-quiz__opt-txt"><span class="pogi-quiz__opt-label">${this.esc(o.label)}</span>${o.desc ? `<span class="pogi-quiz__opt-desc">${this.esc(o.desc)}</span>` : ''}</span>
        <span class="pogi-quiz__opt-tick">${sel ? this.icon('check', 13) : ''}</span>
      </button>`;
    }).join('');
    return `<div class="pogi-quiz__q">
      ${this.#qhead(total)}
      <h2 class="pogi-quiz__qtitle">${this.esc(step.q)}</h2>
      ${step.sub ? `<p class="pogi-quiz__qsub">${this.esc(step.sub)}</p>` : ''}
      <div class="pogi-quiz__opts">${opts}</div>
    </div>`;
  }

  #revealView() {
    const msg = (this._revMsgs && this._revMsgs[this._revI]) || `Sniffing out ${this.dnPoss()} routine…`;
    return `<div class="pogi-quiz__reveal">
      <div class="pogi-quiz__guide pogi-quiz__guide--big">${this.avatarSVG(this.answers, 120)}</div>
      <div class="pogi-quiz__pawtrail"><span></span><span></span><span></span><span></span><span></span></div>
      <p class="pogi-quiz__reveal-txt">${this.esc(msg)}</p>
    </div>`;
  }

  #celebrate() {
    if (this._celebrated) return;
    this._celebrated = true;
    { const a = this.answers, rec = this.recommend(a); this.#track('Completed Quiz', { 'Dog Archetype': (this.ARCHETYPES[this.archetypeKey(a)] || {}).name || '', 'Quiz Result': rec.isKit ? 'Cleanup Kit' : rec.primary, 'Dog Stage': a.stage || '', 'Dog Size': a.size || '' }); }
    if (navigator.vibrate) { try { navigator.vibrate([0, 40, 25, 60]); } catch (_) {} }
    this.#sfxWin();
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const host = this.querySelector('.pogi-quiz__result'); if (!host) return;
    const colors = ['#F26A3D', '#5F6E3A', '#E8B04B', '#3D4724', '#D9572C', '#F4ECDA'];
    const wrap = document.createElement('div'); wrap.className = 'pogi-quiz__confetti'; wrap.setAttribute('aria-hidden', 'true');
    let html = '';
    for (let i = 0; i < 30; i++) {
      const left = (Math.random() * 100).toFixed(1);
      const delay = Math.floor(Math.random() * 280);
      const dur = 1100 + Math.floor(Math.random() * 800);
      if (i % 6 === 0) html += `<span class="pogi-quiz__conf pogi-quiz__conf--paw" style="left:${left}%;animation-delay:${delay}ms;animation-duration:${dur}ms;">🐾</span>`;
      else html += `<span class="pogi-quiz__conf" style="left:${left}%;background:${colors[i % colors.length]};animation-delay:${delay}ms;animation-duration:${dur}ms;"></span>`;
    }
    wrap.innerHTML = html; host.appendChild(wrap);
    setTimeout(() => { try { wrap.remove(); } catch (_) {} }, 2400);
  }

  /* ---------- share (branded card image -> native share, link fallback) ---------- */
  #toast(msg) {
    const host = this.querySelector('.pogi-quiz__result') || this;
    const t = document.createElement('div'); t.className = 'pogi-quiz__toast'; t.textContent = msg;
    host.appendChild(t); requestAnimationFrame(() => t.classList.add('is-on'));
    setTimeout(() => { t.classList.remove('is-on'); setTimeout(() => { try { t.remove(); } catch (_) {} }, 300); }, 1900);
  }
  #svgToImage(svg) {
    return new Promise((res, rej) => { const b = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }); const u = URL.createObjectURL(b); const im = new Image(); im.onload = () => { res(im); URL.revokeObjectURL(u); }; im.onerror = rej; im.src = u; });
  }
  #wrapText(ctx, text, x, y, maxW, lh) {
    const words = String(text || '').split(' '); let line = ''; const lines = [];
    words.forEach((w) => { const t = line ? line + ' ' + w : w; if (ctx.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t; });
    if (line) lines.push(line); lines.forEach((l, i) => ctx.fillText(l, x, y + i * lh)); return lines.length;
  }
  async #cardImage(a, arch, name) {
    const W = 1080, H = 1350, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d'); if (!ctx) return null;
    try { await document.fonts.ready; } catch (_) {}
    const F = "Poppins, 'Segoe UI', Roboto, Arial, sans-serif";
    const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#3f5026'); g.addColorStop(1, '#25321a'); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#F2D27A'; ctx.font = '700 32px ' + F; ctx.fillText('✦  ROUTINE UNLOCKED  ✦', W / 2, 108);
    try { const img = await this.#svgToImage(this.avatarSVG(a, 520, { dirt: 0 })); ctx.drawImage(img, (W - 520) / 2, 150, 520, 520); } catch (_) {}
    try { const seal = await this.#svgToImage(this.sealSVG(arch.name, '#F2D27A')); ctx.save(); ctx.globalAlpha = 0.62; ctx.translate(W - 150, 250); ctx.rotate(-9 * Math.PI / 180); ctx.drawImage(seal, -88, -88, 176, 176); ctx.restore(); } catch (_) {}
    ctx.fillStyle = 'rgba(244,236,218,.7)'; ctx.font = '600 30px ' + F; ctx.fillText((name === 'your dog' ? 'YOUR DOG' : name.toUpperCase()) + ' IS…', W / 2, 730);
    ctx.fillStyle = '#F4ECDA'; ctx.font = '900 78px ' + F; const nLines = this.#wrapText(ctx, arch.name || 'A Pogi', W / 2, 815, W - 150, 80);
    let y = 815 + nLines * 80 + 6; ctx.fillStyle = 'rgba(244,236,218,.82)'; ctx.font = 'italic 30px Georgia, serif'; const tLines = this.#wrapText(ctx, arch.tag || '', W / 2, y, W - 210, 40);
    y += tLines * 40 + 44;
    const pct = this.RARITY[this.archetypeKey(a)] || 12;
    ctx.fillStyle = '#F2D27A'; ctx.font = '700 26px ' + F; ctx.fillText('★ ONLY ' + pct + '% OF DOGS ARE THIS TYPE', W / 2, y); y += 52;
    const stats = this.stats(a), rowH = 56, barX = 600;
    stats.forEach((st) => { ctx.textAlign = 'left'; ctx.fillStyle = '#F4ECDA'; ctx.font = '700 27px ' + F; ctx.fillText(st.label, 210, y + 9); for (let i = 0; i < 5; i++) { ctx.beginPath(); ctx.arc(barX + i * 54, y, 18, 0, Math.PI * 2); ctx.fillStyle = i < st.val ? '#F26A3D' : 'rgba(244,236,218,.22)'; ctx.fill(); } y += rowH; });
    ctx.textAlign = 'center'; ctx.fillStyle = 'rgba(244,236,218,.62)'; ctx.font = '600 26px ' + F; ctx.fillText("What’s your dog?  Take the quiz at pogis.com", W / 2, H - 54);
    const blob = await new Promise((res) => { try { cv.toBlob(res, 'image/png'); } catch (_) { res(null); } });
    return blob ? new File([blob], 'my-dog.png', { type: 'image/png' }) : null;
  }
  async #share() {
    const a = this.answers, arch = this.ARCHETYPES[this.archetypeKey(a)] || {}, name = this.dn();
    this.#track('Shared Quiz', { 'Dog Archetype': arch.name || '' });
    const who = name === 'your dog' ? 'My dog' : name;
    const text = `${who} is ${arch.name || 'a Pogi'}! ${arch.emoji || '🐾'} What kind of dog do you have? Find out:`;
    const q = '/pages/quiz?utm_source=quiz_share&utm_medium=social';
    let url = q; try { url = window.location.origin + q; } catch (_) {}
    let file = null; try { file = await this.#cardImage(a, arch, name); } catch (_) {}
    try {
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], text, url }); return; }
      if (navigator.share) { await navigator.share({ title: "Pogi’s Dog Quiz", text, url }); return; }
    } catch (_) { return; }
    // no native share (desktop): save the card image so it isn't lost, then copy the link
    if (file) { try { const u = URL.createObjectURL(file); const dl = document.createElement('a'); dl.href = u; dl.download = (name === 'your dog' ? 'my-dog' : name.toLowerCase().replace(/[^a-z0-9]/g, '')) + '-quiz-card.png'; document.body.appendChild(dl); dl.click(); dl.remove(); setTimeout(() => { try { URL.revokeObjectURL(u); } catch (_) {} }, 1000); } catch (_) {} }
    try { await navigator.clipboard.writeText(text + ' ' + url); this.#toast(file ? 'Card saved. Link copied.' : 'Link copied. Paste it anywhere.'); }
    catch (_) { this.#toast('Copy this link: ' + url); }
  }

  #result() {
    const a = this.answers, rec = this.recommend(a), P = this.PRODUCTS, c = this.cfg;
    const arch = this.ARCHETYPES[this.archetypeKey(a)] || {};
    const statsRows = this.stats(a).map((st, r) => {
      const dots = Array.from({ length: 5 }, (_, i) => `<span class="pogi-quiz__dot${i < st.val ? ' is-on' : ''}"${i < st.val ? ` style="animation-delay:${260 + r * 130 + i * 60}ms"` : ''}></span>`).join('');
      return `<div class="pogi-quiz__stat"><span class="pogi-quiz__stat-l">${this.esc(st.label)}</span><span class="pogi-quiz__stat-bar">${dots}</span>${st.maxed ? '<span class="pogi-quiz__stat-note">maxed 😌</span>' : ''}</div>`;
    }).join('');
    const slots = rec.isKit ? rec.strong : [rec.primary];
    const list = slots.map((slot, i) => {
      const key = this.prodKey(slot, rec), prod = P[key] || {}, top = !rec.isKit && i === 0;
      const v = this.variantFor(key, a), img = (v && v.img) || prod.img, vlabel = this.variantLabel(key, a);
      return `<div class="pogi-quiz__rcard${top ? ' is-top' : ''}">
        <div class="pogi-quiz__rcard-img">${img ? `<img src="${this.esc(img)}" alt="${this.esc(prod.name)}">` : ''}</div>
        <div class="pogi-quiz__rcard-body">
          ${top ? '<span class="pogi-quiz__rcard-top">Top pick</span>' : ''}
          <h3 class="pogi-quiz__rcard-name">${this.esc(prod.name || key)}</h3>
          <div class="pogi-quiz__rcard-meta">${vlabel ? `<span class="pogi-quiz__rcard-variant">${this.esc(vlabel)}</span>` : ''}${(SHOW_DERIVED && v && v.price) ? `<span class="pogi-quiz__rcard-price">${this.money(v.price)}</span>` : ''}</div>
          <p class="pogi-quiz__rcard-reason">${this.esc(this.reasonFor(key, a))}</p>
          <a class="pogi-quiz__rcard-link" href="${this.esc(this.prodHref(prod, key, a))}">Shop ${this.esc(prod.name || key)}&nbsp;→</a>
        </div>
      </div>`;
    }).join('');
    const pk = this.prodKey(rec.primary, rec);
    const primaryName = (P[pk] || {}).name || rec.primary;
    const cta = `<button type="button" class="pogi-cta pogi-cta--primary pogi-quiz__addcart" data-q="addcart">${rec.isKit ? 'Add my kit to cart' : 'Add to cart'}</button>`;
    const ctaAlt = rec.isKit
      ? `Prefer to subscribe and save 10%? <a href="${this.esc(c.kitUrl || '/pages/subscribe')}">Build a custom kit →</a>`
      : `Or <a href="${this.esc(this.prodHref(P[pk], pk, a))}">subscribe and save 10% →</a>`;
    let subtotal = 0;
    slots.forEach((slot) => { const vv = this.variantFor(this.prodKey(slot, rec), a); if (vv && vv.price) subtotal += vv.price; });
    const dt = c.discountText || '15% off';
    const priceBlock = (SHOW_DERIVED && subtotal > 0)
      ? `<div class="pogi-quiz__price"><span class="pogi-quiz__price-lbl">${rec.isKit ? 'Your kit' : 'Your pick'}</span><span class="pogi-quiz__price-now">${this.money(subtotal)}</span><span class="pogi-quiz__price-save">Unlock ${this.esc(dt)} your first order below ↓</span></div>`
      : '';
    // smart S&S nudge: shows for the recurring/high-consumption answers, with the
    // opening line tailored to their strongest signal (runout wins). Hidden for
    // occasional/low-burn results so it never reads as a blanket upsell.
    let subLead = '';
    if (a.bags === 'runout') subLead = 'You said never running out.';
    else if (a.stage === 'pack') subLead = 'A whole pack burns through supplies fast.';
    else if (a.indoor === 'apartment') subLead = 'Indoor potty is the daily routine.';
    else if (a.indoor === 'training') subLead = 'House-training takes a steady supply.';
    const subNudge = subLead
      ? `<div class="pogi-quiz__subnudge"><span class="pogi-quiz__subnudge-ic" aria-hidden="true">🔄</span><div class="pogi-quiz__subnudge-body"><p><strong>${subLead}</strong> Subscribe &amp; Save delivers ${this.esc(this.dnPoss())} routine automatically and saves 10% every order. Skip or cancel anytime.</p><button type="button" class="pogi-quiz__subbtn" data-q="subscribe">${rec.isKit ? 'Subscribe &amp; Save my kit' : 'Subscribe &amp; Save 10%'}</button></div></div>`
      : '';
    const capture = this.cap.sent
      ? `<div class="pogi-quiz__cap pogi-quiz__cap--done">
          <div class="pogi-quiz__unwrap">${this.esc(dt).toUpperCase()} UNLOCKED 🎉</div>
          <h3>You’re in!</h3>
          <p>${this.esc(this.dnPoss())} routine is on its way. Check your inbox for your ${this.esc(dt)} code.</p>
        </div>`
      : `<form class="pogi-quiz__cap" data-q-capture>
          <div class="pogi-quiz__cap-head">${this.icon('mail', 20)}<h3>Unlock ${this.esc(dt)} + save ${this.esc(this.dnPoss())} routine</h3></div>
          <p>Drop your email and we’ll send the routine plus a one-time <strong>${this.esc(dt)}</strong> your first order. No spam, unsubscribe anytime.</p>
          <div class="pogi-quiz__cap-fields">
            <input name="email" type="email" required placeholder="Email address" class="pogi-quiz__cap-email">
            <button type="submit" class="pogi-quiz__cap-submit">Unlock my ${this.esc(dt)}</button>
          </div>
        </form>`;
    return `<div class="pogi-quiz__result">
      <div class="pogi-quiz__seal">${this.sealSVG(arch.name)}</div>
      <div class="pogi-quiz__victory"><span class="pogi-quiz__vstar">✦</span><span class="pogi-quiz__vtext">Routine unlocked</span><span class="pogi-quiz__vstar">✦</span></div>
      <div class="pogi-quiz__card">
        <div class="pogi-quiz__card-id">
          <div class="pogi-quiz__avatar">${this.avatarSVG(a, 132, { dirt: 0, sparkle: true })}</div>
          <p class="pogi-quiz__eyebrow">${this.esc(this.dn())} is…</p>
          <h2 class="pogi-quiz__arch">${this.esc(arch.name || 'Your match')}</h2>
          <p class="pogi-quiz__arch-tag">${this.esc(arch.tag || '')}</p>
        </div>
        <div class="pogi-quiz__stats">${statsRows}<p class="pogi-quiz__rarity">★ Only ${this.RARITY[this.archetypeKey(a)] || 12}% of dogs are this type</p></div>
      </div>
      <p class="pogi-quiz__routine-h">${this.esc(this.dnPoss())} routine</p>
      ${arch.kit ? `<p class="pogi-quiz__kit-pill">${this.esc(arch.kit)}</p>` : ''}
      <div class="pogi-quiz__rcards${slots.length > 1 ? ' is-kit' : ' is-single'}">${list}</div>
      ${priceBlock}
      ${subNudge}
      <div class="pogi-quiz__result-cta">${cta}<button type="button" class="pogi-cta pogi-cta--pill" data-q="share">Share ${this.esc(this.dnPoss())} card</button><button type="button" class="pogi-cta pogi-cta--pill" data-q="restart">Retake quiz</button></div>
      <p class="pogi-quiz__cta-alt">${ctaAlt}</p>
      ${capture}
    </div>`;
  }
}

if (!customElements.get('pogi-quiz')) customElements.define('pogi-quiz', PogiQuiz);
